import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";

// Two brugo.in lead settings, both in Firestore app_config:
//   operating_hours { enabled, start "09:00", end "20:00" }  India time. Outside these hours the
//     customer still pays; the request is held and floats at the next opening time.
//   lead_emails { enabled, recipients[] }  every new website request is emailed here by the
//     emailNewRequest Cloud Function (sent from hello@brugo.in).
const DEFAULT_HOURS = { enabled: true, start: "09:00", end: "20:00" };
const DEFAULT_EMAILS = { enabled: true, recipients: ["hello@brugo.in"] };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const fmt12 = (hhmm) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (!Number.isFinite(h)) return hhmm;
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

export default function LeadSettings() {
  const { user } = useAuth();
  const [hours, setHours] = useState(null);
  const [emails, setEmails] = useState(null);
  const [hForm, setHForm] = useState(DEFAULT_HOURS);
  const [eForm, setEForm] = useState({ enabled: true, text: "hello@brugo.in" });
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const u1 = adapter.subscribeDoc("app_config", "operating_hours", (d) => {
      setHours(d);
      setHForm({ ...DEFAULT_HOURS, ...(d || {}) });
    });
    const u2 = adapter.subscribeDoc("app_config", "lead_emails", (d) => {
      setEmails(d);
      const v = { ...DEFAULT_EMAILS, ...(d || {}) };
      setEForm({ enabled: v.enabled !== false, text: (v.recipients || []).join("\n") });
    });
    return () => { u1 && u1(); u2 && u2(); };
  }, []);

  const saveHours = async () => {
    if (!/^\d{2}:\d{2}$/.test(hForm.start) || !/^\d{2}:\d{2}$/.test(hForm.end) || hForm.end <= hForm.start) {
      toast.error("Closing time must be after opening time");
      return;
    }
    setBusy("hours");
    try {
      await adapter.set("app_config", "operating_hours", {
        enabled: !!hForm.enabled, start: hForm.start, end: hForm.end, timezone: "Asia/Kolkata",
        updated_at: new Date().toISOString(), updated_by: user?.email || null,
      });
      toast.success("Operating hours saved");
    } catch (e) { toast.error(e?.code === "permission-denied" ? "Only an admin can change this" : (e.message || "Failed")); }
    finally { setBusy(null); }
  };

  const saveEmails = async () => {
    const list = [...new Set(eForm.text.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean))];
    const bad = list.filter((x) => !EMAIL_RE.test(x));
    if (bad.length) { toast.error(`Not an email: ${bad.join(", ")}`); return; }
    if (!list.length) { toast.error("Add at least one email"); return; }
    setBusy("emails");
    try {
      await adapter.set("app_config", "lead_emails", {
        enabled: !!eForm.enabled, recipients: list,
        updated_at: new Date().toISOString(), updated_by: user?.email || null,
      });
      toast.success("Lead email settings saved");
    } catch (e) { toast.error(e?.code === "permission-denied" ? "Only an admin can change this" : (e.message || "Failed")); }
    finally { setBusy(null); }
  };

  return (
    <div className="flex flex-wrap gap-6 items-start" data-testid="lead-settings">
      <Card className="p-6 max-w-md w-full" data-testid="operating-hours-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Icons.Clock className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Operating hours</h3></div>
          <Switch checked={!!hForm.enabled} onCheckedChange={(v) => setHForm((f) => ({ ...f, enabled: v }))} data-testid="hours-enabled" />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {hForm.enabled
            ? `Requests paid outside ${fmt12(hForm.start)} to ${fmt12(hForm.end)} are held and go to professionals at ${fmt12(hForm.start)}.`
            : "Off: every request goes to professionals immediately, at any time."}
        </p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="space-y-1.5"><Label>Opens (India time)</Label><Input type="time" value={hForm.start} onChange={(e) => setHForm((f) => ({ ...f, start: e.target.value }))} data-testid="hours-start" /></div>
          <div className="space-y-1.5"><Label>Closes (India time)</Label><Input type="time" value={hForm.end} onChange={(e) => setHForm((f) => ({ ...f, end: e.target.value }))} data-testid="hours-end" /></div>
        </div>
        <Button className="mt-4 w-full" onClick={saveHours} disabled={busy === "hours"} data-testid="hours-save">Save hours</Button>
        <p className="text-[11px] text-muted-foreground mt-2">{hours ? `Last saved by ${hours.updated_by || "-"}` : "Not saved yet: brugo.in uses 9:00 AM to 8:00 PM."} · app_config/operating_hours</p>
      </Card>

      <Card className="p-6 max-w-md w-full" data-testid="lead-emails-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Icons.Mail className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Lead emails</h3></div>
          <Switch checked={!!eForm.enabled} onCheckedChange={(v) => setEForm((f) => ({ ...f, enabled: v }))} data-testid="lead-emails-enabled" />
        </div>
        <p className="text-sm text-muted-foreground mt-1">Every new brugo.in request is emailed with the customer's name, phone, address, services and answers. Sent from hello@brugo.in.</p>
        <div className="space-y-1.5 mt-4">
          <Label>Send to (one email per line)</Label>
          <Textarea rows={3} value={eForm.text} onChange={(e) => setEForm((f) => ({ ...f, text: e.target.value }))} data-testid="lead-emails-recipients" />
        </div>
        <Button className="mt-4 w-full" onClick={saveEmails} disabled={busy === "emails"} data-testid="lead-emails-save">Save email settings</Button>
        <p className="text-[11px] text-muted-foreground mt-2">{emails ? `Last saved by ${emails.updated_by || "-"}` : "Not saved yet: emails go to hello@brugo.in."} · app_config/lead_emails</p>
      </Card>
    </div>
  );
}
